import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";
import store, { IAppState } from "../Store";
import Datastore from "../../communication/Datastore";
import { OrganisationUnit } from "../../types/core/OrganisationUnit";

// Define the type for our state store entry
type OrgUnitsState = {
    orgUnits: OrganisationUnit[];
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: OrgUnitsState = {
    orgUnits: [],
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const orgUnitsSlice = createSlice({
    name: "orgUnits",
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    orgUnits: rawData?.payload?.orgUnits?.orgUnits || [],
                    status: !rawData?.payload?.orgUnits?.orgUnits
                        ? "idle"
                        : "succeeded",
                    error: null,
                };
            })
            .addCase(fetchOrgUnits.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchOrgUnits.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.orgUnits = action.payload;
            })
            .addCase(fetchOrgUnits.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(addOrgUnit.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(addOrgUnit.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.orgUnits = state.orgUnits.concat(action.payload);
            })
            .addCase(addOrgUnit.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateOrgUnit.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(updateOrgUnit.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                const unitIds = action.payload.map(c => c.id);
                state.orgUnits = state.orgUnits
                    .filter(c => !unitIds.includes(c.id))
                    .concat(action.payload);
            })
            .addCase(updateOrgUnit.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

const {} = orgUnitsSlice.actions;

export const initialiseOrgUnitDetails = createAsyncThunk(
    "orgUnits/initialiseOrgUnitDetails",
    (_, thunkAPI) => thunkAPI.dispatch(fetchOrgUnits()),
);

// This is a thunk, they are used to load the state
export const fetchOrgUnits = createAsyncThunk(
    "orgUnits/fetchOrgUnits",
    () => Datastore.getType<OrganisationUnit[]>("api/OrganisationUnits"),
    {
        condition: (_, api) =>
            store.getState().db.orgUnits.status === "idle",
    },
);
export const addOrgUnit = createAsyncThunk(
    "orgUnits/addOrgUnit",
    (payload: OrganisationUnit[]) =>
        Datastore.postType<OrganisationUnit[]>(
            "api/OrganisationUnits",
            payload,
        ),
    {
        condition: (_, api) =>
            store.getState().db.orgUnits.status === "succeeded",
    },
);
export const updateOrgUnit = createAsyncThunk(
    "orgUnits/updateOrgUnit",
    (payload: OrganisationUnit[]) =>
        Datastore.putType<OrganisationUnit[]>("api/OrganisationUnits", payload),
    {
        condition: (_, api) =>
            store.getState().db.orgUnits.status === "succeeded",
    },
);

// We have to export the reducer as this is added to the state store
export default orgUnitsSlice.reducer;
